import React, { useRef } from "react";
import { Col, Row, Hr, View } from "@amzn/stencil-react-components/layout";
import { H2, Text } from "../../typography";
import "../../sda/styles.scss";
import { Link } from "@amzn/stencil-react-components/link";
import useArb from "../../../hooks/use-arb";
import Markdown from "markdown-to-jsx";
import { getRegionFromCookie, isSpanishLocale } from "../../../utility/locale-helper";
import { USCIS_URL, USCIS_URL_ES } from "../../../config/urls";
import { Button, ButtonSize } from "@amzn/stencil-react-components/button";
import moment from "moment";
import { NheDetails } from "../../../utility/application-data";
import { useNavigatorWithURLReady } from "../../../hooks/use-navigator";
import {
  IconCalendarFill,
  IconCheckCircleFill,
  IconLocationFill,
  IconHomeFill,
  IconAccessibilityPersonCircle,
  IconDesktop,
} from "@amzn/stencil-react-components/icons";
import { Expander, ExpanderChevronPosition } from "@amzn/stencil-react-components/expander";
import { useReactToPrint } from "react-to-print";
import "./style.scss";
import { processTime } from "../../../utility/time-processor";
import { getChimeLinkForVirtualNHE, isCountryWithVirtualNHE, isCountryWithoutI9 } from "../../../helpers/utils";
import { LAUNCHED_COUNTRY } from "../../../config/appConfigConstants";
import { GLOBAL_CONTACT_US_URL } from "../../../utility/constants";

interface PreHireAppointmentDetailsProps {
  nheDetails: NheDetails;
  eligibleForAppointmentRescheduling: boolean;
  appointmentSelfServiceURL: string;
}

export const PreHireAppointmentDetails = ({
  nheDetails,
  eligibleForAppointmentRescheduling,
  appointmentSelfServiceURL,
}: PreHireAppointmentDetailsProps) => {
  const bundle = useArb("HVHChecklistPantherResource");
  const isWithVirtualNHE = isCountryWithVirtualNHE(getRegionFromCookie() as LAUNCHED_COUNTRY);
  const isWithoutI9 = isCountryWithoutI9(getRegionFromCookie() as LAUNCHED_COUNTRY);

  /** constants for what to expect section */
  const whatToExpect = bundle.getMessage("Checklist-PreHireDetails-WhatToExpect");
  const whatToExpectContent = isWithVirtualNHE
    ? bundle.getMessage("Checklist-PreHireDetails-WhatToExpect-Content-Virtual-NHE")
    : bundle.getMessage("Checklist-PreHireDetails-WhatToExpect-Content");
  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => {
      return componentRef.current;
    },
  });
  const printPage = bundle.getMessage("Checklist-PreHireDetails-PrintPage");

  /** constants for what to bring section */
  const whatToBring = bundle.getMessage("Checklist-PreHireDetails-WhatToBring");
  const viewAllValidDocuments = bundle.getMessage("Checklist-PreHireDetails-WhatToBring-Link");
  const uscis_link = isSpanishLocale() ? USCIS_URL_ES : USCIS_URL;
  const documentsLink = (
    <Link target="_blank" href={uscis_link}>
      {viewAllValidDocuments}
    </Link>
  );
  const viewDocumentsLink = isWithoutI9
    ? bundle.getMessage("Checklist-PreHireDetails-WhatToBring-Content-Without-I9")
    : bundle.formatMessage("Checklist-PreHireDetails-WhatToBring-Content", {
        documentLink: documentsLink,
      });

  /** constants for when and where section */
  const whenAndWhere = bundle.getMessage("Checklist-PreHireDetails-WhenAndWhere");
  const googlePlaceURL = "http://maps.google.com/?q=";
  const googleMapLink = (
    <Link fontSize={"T200"} href={googlePlaceURL + nheDetails.address} target={"_blank"}>
      {nheDetails.address}
    </Link>
  );
  const startTime = nheDetails.nheTimeRange?.split("-")[0];
  const processedStartTime = processTime(startTime);
  const startDateAndStartTime = nheDetails.nheDate?.toString() + " " + processedStartTime;
  const timeFormat = "MMM DD, YYYY hh:mm A";
  const plainStartDateAndStartTime = moment(startDateAndStartTime, timeFormat); // Display site local time

  const date = bundle.formatMessage("Checklist-PreHireDetails-Date", {
    date: plainStartDateAndStartTime,
  });
  const time = bundle.formatMessage("Checklist-PreHireDetails-Time", {
    time: plainStartDateAndStartTime,
  });
  const locationDetails = isWithVirtualNHE
    ? bundle.formatMessage("Checklist-PreHireDetails-Location-Virtual-NHE", {
        address: getChimeLinkForVirtualNHE(nheDetails.address, bundle.getMessage("Checklist-Common-Virtual")),
      })
    : bundle.formatMessage("Checklist-PreHireDetails-Location", {
        address: googleMapLink,
      });
  const entrance = bundle.formatMessage("Checklist-PreHireDetails-Entrance", {
    entrance: nheDetails.entrance,
  });
  const parking = bundle.formatMessage("Checklist-PreHireDetails-Parking", {
    parking: nheDetails.parking,
  });
  const access = bundle.formatMessage("Checklist-PreHireDetails-Access", {
    access: nheDetails.access,
  });
  const cannotAttend = bundle.formatMessage("Checklist-PreHireDetails-CannotAttend");
  const reschedule = bundle.getMessage("Checklist-AppointmentCard-ButtonText2");

  /** constants for request accommodations section */
  const requestAccommodations = bundle.getMessage("Checklist-PreHireDetails-RequestAccommodations");
  const pleaseClickHere = bundle.getMessage("checklist-General-PleaseClickHere");

  const supportLink = (
    <Link target="_blank" href={GLOBAL_CONTACT_US_URL}>
      {pleaseClickHere}
    </Link>
  );
  const specialAssistDetails = bundle.formatMessage("Checklist-PreHireDetails-AssistDetails1", {
    supportLink: supportLink,
  });

  const CustomRow = (children: any) => <Row gridGap="0.5rem">{children}</Row>;

  return (
    <Col className="flyout-content" gridGap="1rem" ref={componentRef}>
      <Row>
        <Col gridGap="0.5rem">
          <Row justifyContent="space-between" alignItems="center">
            <H2>{whatToExpect}</H2>
            <Button className="print-link" onClick={handlePrint}>
              {printPage}
            </Button>
          </Row>
          {isWithVirtualNHE ? (
            <Text fontSize="T100" className="what-to-expect-content">
              <Markdown>{whatToExpectContent}</Markdown>
            </Text>
          ) : (
            <Text fontSize="T100">{whatToExpectContent}</Text>
          )}
        </Col>
      </Row>
      <Row>
        <Col gridGap="0.5rem">
          <H2>{whatToBring}</H2>
          <Text fontSize="T100">{viewDocumentsLink}</Text>
        </Col>
      </Row>
      <Row>
        <View width="100%">
          <Expander titleText={whenAndWhere} chevronPosition={ExpanderChevronPosition.Trailing}>
            <Col gridGap="1rem">
              <CustomRow
                {...(
                  <>
                    <IconCalendarFill />
                    <Text>{date}</Text>
                  </>
                )}
              ></CustomRow>
              <CustomRow
                {...(
                  <>
                    <IconCheckCircleFill />
                    <Text>{time}</Text>
                  </>
                )}
              ></CustomRow>
              <CustomRow
                {...(
                  <>
                    {isWithVirtualNHE ? <IconDesktop /> : <IconLocationFill />}
                    <Text>{locationDetails}</Text>
                  </>
                )}
              ></CustomRow>
              {nheDetails.entrance && (
                <CustomRow
                  {...(
                    <>
                      <IconHomeFill />
                      <Text>{entrance}</Text>
                    </>
                  )}
                ></CustomRow>
              )}
              {nheDetails.parking && (
                <CustomRow
                  {...(
                    <>
                      <IconHomeFill />
                      <Text>{parking}</Text>
                    </>
                  )}
                ></CustomRow>
              )}
              {nheDetails.access && (
                <CustomRow
                  {...(
                    <>
                      <IconAccessibilityPersonCircle />
                      <Text>{access}</Text>
                    </>
                  )}
                ></CustomRow>
              )}
              {eligibleForAppointmentRescheduling && (
                <Row justifyContent="space-between" alignItems="center">
                  <Text fontSize="T100">
                    <strong>{cannotAttend}</strong>
                  </Text>
                  <Button
                    as={"a"}
                    size={ButtonSize.Small}
                    onClick={useNavigatorWithURLReady(appointmentSelfServiceURL)}
                  >
                    {reschedule}
                  </Button>
                </Row>
              )}
            </Col>
          </Expander>
        </View>
      </Row>
      <Hr />
      <Row>
        <View width="100%">
          <Expander titleText={requestAccommodations} chevronPosition={ExpanderChevronPosition.Trailing}>
            <Col gridGap="1rem">
              <Row gridGap="0.5rem">
                <Text>{specialAssistDetails}</Text>
              </Row>
            </Col>
          </Expander>
        </View>
      </Row>
      <Hr />
    </Col>
  );
};
